/* eslint-disable @typescript-eslint/no-explicit-any -- dynamic PostgREST/Supabase rows are shaped at runtime; typed at the boundary. TODO: replace with generated row types. */
/**
 * useBucketDrilldown — everything to render a detail modal for one bucket:
 * learned style brief, top posts, recent drafts, best references,
 * sub-category counts.
 */
import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';

export interface BucketDetail {
  slug: string;
  name: string;
  description: string | null;
  color_accent: string | null;
  style_prompt: string | null;
  mood: string | null;
  brand_voice_notes: string | null;
  learned_style_notes: string | null;
  learned_updated_at: string | null;
  learned_top_performers: any;
  target_monthly: number | null;
}

export interface RecentDraft {
  id: string;
  status: string;
  post_kind: string;
  caption: string | null;
  updated_at: string;
  published_at: string | null;
  windsor_media_id: string | null;
  thumb_key: string | null;
}

export interface TopPostInBucket {
  media_id: string;
  permalink: string | null;
  thumbnail_url: string | null;
  caption: string | null;
  published_at: string;
  like_count: number;
  comments_count: number;
  reach: number | null;
  total_interactions: number | null;
}

export interface BucketRef {
  id: string;
  drive_file_id: string | null;
  drive_thumbnail_link: string | null;
  original_name: string | null;
  sub_category: string | null;
  posts_used_in?: number;
  avg_engagement_pct?: number | null;
}

export function useBucketDrilldown(slug: string | null) {
  return useQuery({
    queryKey: ['bucket-drilldown', slug],
    enabled: !!slug,
    queryFn: async () => {
      if (!slug) return null;
      const [bucketRes, draftsRes, refPerfRes, refsRes] = await Promise.all([
        marketing.from('content_bucket')
          .select('slug,name,description,color_accent,style_prompt,mood,brand_voice_notes,learned_style_notes,learned_updated_at,learned_top_performers,target_monthly')
          .eq('slug', slug)
          .single(),
        marketing.from('content_draft')
          .select('id,status,post_kind,caption,updated_at,published_at,windsor_media_id,slides:content_draft_slide(slide_index,image_r2_key)')
          .eq('bucket_slug', slug)
          .neq('status', 'archived')
          .order('updated_at', { ascending: false })
          .limit(12),
        marketing.from('content_reference_performance_v').select('*'),
        marketing.from('content_bucket_reference')
          .select('id,drive_file_id,drive_thumbnail_link,original_name,sub_category')
          .eq('bucket_slug', slug)
          .eq('is_composition_anchor', false)
          .limit(50),
      ]);

      const bucket = (bucketRes.data ?? null) as BucketDetail | null;

      const drafts: RecentDraft[] = (draftsRes.data ?? []).map((d: any) => ({
        id: d.id, status: d.status, post_kind: d.post_kind, caption: d.caption,
        updated_at: d.updated_at, published_at: d.published_at, windsor_media_id: d.windsor_media_id,
        thumb_key: (d.slides ?? []).find((s: any) => s.slide_index === 0)?.image_r2_key ?? null,
      }));

      // top posts: join published draft media_ids to meta_posts and rank
      const pubMediaIds = drafts.filter((d) => d.status === 'published' && d.windsor_media_id).map((d) => d.windsor_media_id as string);
      let topPosts: TopPostInBucket[] = [];
      if (pubMediaIds.length > 0) {
        const inList = pubMediaIds.map((id) => `"${id}"`).join(',');
        const { data } = await marketing.from('meta_posts')
          .select('media_id,permalink,thumbnail_url,caption,published_at,like_count,comments_count,reach,total_interactions')
          .filter('media_id', 'in', `(${inList})`)
          .order('total_interactions', { ascending: false, nullsFirst: false })
          .limit(6);
        topPosts = ((data ?? []) as any[]) as TopPostInBucket[];
      }

      const perfMap = new Map<string, { posts_used_in: number; avg_engagement_pct: number | null }>();
      for (const r of (refPerfRes.data ?? []) as any[]) perfMap.set(r.reference_id, { posts_used_in: r.posts_used_in, avg_engagement_pct: r.avg_engagement_pct });

      const refs: BucketRef[] = (refsRes.data ?? []).map((r: any) => ({
        ...r, ...(perfMap.get(r.id) ?? {}),
      }));
      // Sort by performance desc, refs with no perf go last
      refs.sort((a, b) => {
        const aa = a.avg_engagement_pct ?? -1;
        const bb = b.avg_engagement_pct ?? -1;
        return bb - aa;
      });

      // Sub-category breakdown from refs list
      const subMap = new Map<string, { name: string; count: number }>();
      for (const r of refs) {
        const key = r.sub_category ?? '(unfiled)';
        const existing = subMap.get(key) ?? { name: key, count: 0 };
        existing.count += 1;
        subMap.set(key, existing);
      }
      const subCategories = [...subMap.values()].sort((a, b) => b.count - a.count);

      return { bucket, drafts, topPosts, refs, subCategories };
    },
    staleTime: 60_000,
  });
}
